import React, { useEffect, useMemo, useRef, useState } from "react";
import { ExternalLink, Facebook, RefreshCw } from "lucide-react";
import "../../pages/facebook/FacebookUpdatesPage.css";

const PAGE = "https://www.facebook.com/symmachinery";
const SDK_URL =
  "https://connect.facebook.net/en_US/sdk.js#xfbml=1&version=v24.0";
let sdkLoadingPromise;

function loadFacebookSdk() {
  if (window.FB?.XFBML?.parse) return Promise.resolve();
  if (sdkLoadingPromise) return sdkLoadingPromise;

  sdkLoadingPromise = new Promise((resolve, reject) => {
    let script = document.querySelector('script[data-sym-facebook-sdk="true"]');
    if (!script) {
      script = document.createElement("script");
      script.src = SDK_URL;
      script.async = true;
      script.defer = true;
      script.dataset.symFacebookSdk = "true";
    }

    const finish = () => {
      if (window.FB?.XFBML?.parse) resolve();
      else reject(new Error("Facebook SDK could not initialize"));
    };
    script.addEventListener("load", finish, { once: true });
    script.addEventListener(
      "error",
      () => reject(new Error("Facebook SDK blocked")),
      { once: true },
    );
    if (!script.isConnected) document.body.appendChild(script);
    else if (window.FB?.XFBML?.parse) finish();
  }).catch((error) => {
    sdkLoadingPromise = undefined;
    throw error;
  });
  return sdkLoadingPromise;
}

/**
 * Meta owns the contents of both embedding methods. The SDK can work when the
 * iframe timeline is blank, but neither method guarantees a visible feed.
 */
export default function FacebookPageEmbed({ compact = false }) {
  const rootRef = useRef(null);
  const pluginRef = useRef(null);
  const [mode, setMode] = useState("sdk");
  const [failed, setFailed] = useState(false);
  const [width, setWidth] = useState(1000);
  const height = compact ? 540 : 900;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const updateWidth = () => {
      const next = Math.max(
        320,
        Math.min(1000, Math.floor(root.clientWidth) || 1000),
      );
      setWidth((previous) => (previous === next ? previous : next));
    };
    updateWidth();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateWidth)
        : null;
    if (observer) observer.observe(root);
    else window.addEventListener("resize", updateWidth);
    return () => {
      if (observer) observer.disconnect();
      else window.removeEventListener("resize", updateWidth);
    };
  }, []);

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams({
      href: PAGE,
      tabs: "timeline",
      width: String(width),
      height: String(height),
      small_header: "false",
      adapt_container_width: "true",
      hide_cover: "false",
      show_facepile: "false",
    });
    return `https://www.facebook.com/plugins/page.php?${params}`;
  }, [height, width]);

  useEffect(() => {
    if (mode !== "sdk") return undefined;
    let cancelled = false;
    setFailed(false);
    loadFacebookSdk()
      .then(() => {
        if (cancelled) return;
        if (pluginRef.current && window.FB?.XFBML?.parse) {
          window.FB.XFBML.parse(pluginRef.current);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, width, height]);

  return (
    <div
      ref={rootRef}
      className={`sym-fb-embed ${compact ? "sym-fb-embed--compact" : ""}`}
    >
      <div className="sym-fb-embed-content">
        {failed ? (
          <div className="sym-fb-embed-fallback" role="status">
            <Facebook size={29} />
            <strong>Facebook feed isn't available in this browser</strong>
            <p>
              Try the alternate viewer, disable tracking protection for this
              page, or open Facebook directly.
            </p>
          </div>
        ) : mode === "sdk" ? (
          <div
            ref={pluginRef}
            className="sym-fb-sdk-host"
            key={`sdk-${width}-${height}`}
          >
            <div
              className="fb-page"
              data-href={PAGE}
              data-show-posts="true"
              data-width={width}
              data-height={height}
              data-small-header="false"
              data-adapt-container-width="true"
              data-hide-cover="false"
              data-show-facepile="false"
            >
              <blockquote cite={PAGE} className="fb-xfbml-parse-ignore">
                <a href={PAGE} target="_blank" rel="noopener noreferrer">
                  SYM Machinery on Facebook
                </a>
              </blockquote>
            </div>
          </div>
        ) : (
          <iframe
            title="SYM Machinery Facebook page timeline"
            src={iframeSrc}
            width={width}
            height={height}
            loading={compact ? "lazy" : "eager"}
            scrolling="no"
            allow="autoplay; encrypted-media; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>
  );
}
